"use strict";
import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResolvedPath } from "react-router";
import { EmptyArray, EmptyObject } from "capitalrx-components-2.0/lib/utilities";
import { AuthContext, AuthTokenProvider } from "capitalrx-shell-auth";
import classNames from "classnames";
import styles from "./Shell.module.css";
import { AsyncWrapper } from "./AsyncWrapper";
import { ContentLayout } from "./ContentLayout";
import { ErrorPage } from "./ErrorPage";
import { Routes } from "./Routes";
import { DefaultProviderFunction, clearFederationRuntimeCache, fetchAvailableModules, loadModules } from "./api";
import { ContentChannel } from "./components";
import { OwnerModuleContext, ShellContext } from "./context";
import { getAnalyticsIdFromAuthToken } from "./getAnalyticsIdFromAuthToken";
import { useEvent } from "./hooks";
import { HotkeyRegistryContext, createHotkeyRegistryManager } from "./hotkeyRegistry";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { AlertProvider, OverlayContainer, TooltipContainer, HotKeyRoot } from "capitalrx-components-2.0/lib/general";
const shellHotkeyMetadata = {
  id: "shell",
  name: "Shell",
  permissions: ["hotkeys:register"]
};
const ShellHotkeyWrapper = ({ children }) => {
  return /* @__PURE__ */ jsx(HotKeyRoot, { children });
};
const LifeCycleProcess = {
  loadAvailableCoreModules: "clearCoreModules",
  clearCoreModules: "initializeCoreModules",
  initializeCoreModules: "loadAvailableAppModules",
  loadAvailableAppModules: "clearAppModules",
  clearAppModules: "initializeAppModules",
  initializeAppModules: "renderRoutes",
  renderRoutes: null
};
const LoadingStateNames = {
  loadAvailableCoreModules: "Fetch Core Modules",
  clearCoreModules: "Clear Core Modules",
  initializeCoreModules: "Initialize Core Modules",
  loadAvailableAppModules: "Fetch Application Modules",
  clearAppModules: "Clear Application Modules",
  initializeAppModules: "Initialize Application Modules",
  renderRoutes: "Render Routes"
};
export const Shell = (props) => {
  const { children } = props;
  const path = useResolvedPath("").pathname;
  const loadingCountRef = useRef(0);
  const coreModulePaths = useRef(null);
  const appModulePaths = useRef(null);
  const coreModules = useRef(null);
  const appModules = useRef(null);
  const hotkeyRegistryManager = useMemo(() => {
    const manager = createHotkeyRegistryManager();
    if (typeof window !== "undefined") {
      const hostWindow = window;
      hostWindow.__CAPITAL_RX_HOTKEY_REGISTRY__ = manager;
      if (!hostWindow.__CAPITAL_RX_MODULE_METADATA__) {
        hostWindow.__CAPITAL_RX_MODULE_METADATA__ = shellHotkeyMetadata;
      }
    }
    return manager;
  }, []);
  const deferredPermissions = useRef(null);
  const [locked, setLocked] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [authRefreshToken, setAuthRefreshToken] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [dynamicSubscriptions, setDynamicSubscriptions] = useState(EmptyObject);
  const [analyticsId, setAnalyticsId] = useState(null);
  const [currentLoadingStage, setCurrentLoadingStage] = useState("loadAvailableCoreModules");
  const [coreChannelSubscriptions, setCoreChannelSubscriptions] = useState(
    () => DefaultProviderFunction
  );
  const [appChannelSubscriptions, setAppChannelSubscriptions] = useState(() => DefaultProviderFunction);
  const [hasError, setHasError] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const cancelLoadRef = useRef(null);
  const onLoadingStateChange = useEvent(props.onLoadingStateChange);
  const onAnalyticsIdChange = useEvent(props.onAnalyticsIdChange);
  const loadFromStage = useCallback((stage) => {
    let cancel = false;
    let failed = false;
    async function waitForLoadingToComplete() {
      await new Promise((resolve) => {
        function doCheck() {
          if (loadingCountRef.current === 0) {
            resolve();
          } else setTimeout(doCheck);
        }
        doCheck();
      });
    }
    async function initializeCoreModules() {
      try {
        const [newModules, newChannelSubscriptions] = await loadModules("core", coreModulePaths.current, null);
        if (cancel) return;
        coreModules.current = newModules;
        setCoreChannelSubscriptions(() => newChannelSubscriptions);
      } catch {
        setHasError(true);
      }
    }
    async function initializeAppModules() {
      const permissionsToUse = deferredPermissions.current ? deferredPermissions.current : permissions;
      deferredPermissions.current = null;
      const [newModules, newChannelSubscriptions] = await loadModules("app", appModulePaths.current, permissionsToUse);
      if (cancel) return;
      appModules.current = newModules;
      setAppChannelSubscriptions(() => newChannelSubscriptions);
    }
    async function runStage(stage2) {
      await waitForLoadingToComplete();
      if (cancel) return;
      console.log(`[Shell] ${LoadingStateNames[stage2]}`);
      switch (stage2) {
        case "loadAvailableCoreModules":
          coreModulePaths.current = await fetchAvailableModules("core");
          if (coreModulePaths.current === null) failed = true;
          break;
        case "loadAvailableAppModules":
          appModulePaths.current = await fetchAvailableModules("app");
          if (appModulePaths.current === null) failed = true;
          break;
        case "clearCoreModules":
          clearFederationRuntimeCache(coreModulePaths.current);
          coreModules.current = null;
          break;
        case "initializeCoreModules":
          await initializeCoreModules();
          break;
        case "clearAppModules":
          appModules.current = null;
          break;
        case "initializeAppModules":
          await initializeAppModules();
          break;
        case "renderRoutes":
          break;
        default:
          break;
      }
    }
    async function run() {
      let currentStage = stage;
      while (currentStage !== null) {
        await runStage(currentStage);
        if (cancel) return;
        if (failed) {
          setHasError(true);
          return;
        }
        setCurrentLoadingStage(currentStage);
        onLoadingStateChange(currentStage);
        if (deferredPermissions.current && currentStage === "renderRoutes") currentStage = "clearAppModules";
        else currentStage = LifeCycleProcess[currentStage];
      }
    }
    run().catch((err) => {
      console.error("[Shell] Module loading failed.", err);
      setHasError(true);
    });
    return () => {
      cancel = true;
    };
  }, []);
  useEffect(() => {
    if (authToken === null || isLoggingOut) {
      return;
    }
    deferredPermissions.current = permissions;
    if (currentLoadingStage === "renderRoutes") {
      if (cancelLoadRef.current) {
        cancelLoadRef.current();
        cancelLoadRef.current = null;
      }
      cancelLoadRef.current = loadFromStage("clearAppModules");
      return () => {
        if (!cancelLoadRef.current) return;
        cancelLoadRef.current();
        cancelLoadRef.current = null;
      };
    }
  }, [permissions]);
  useEffect(() => {
    if (authToken !== null && isLoggingOut) {
      setIsLoggingOut(false);
    }
  }, [authToken, isLoggingOut]);
  useEffect(() => {
    if (authToken === null) {
      setAnalyticsId(null);
      return;
    }
    setAnalyticsId(getAnalyticsIdFromAuthToken(authToken));
  }, [authToken]);
  useEffect(() => {
    if (cancelLoadRef.current) {
      cancelLoadRef.current();
      cancelLoadRef.current = null;
    }
    cancelLoadRef.current = loadFromStage("loadAvailableCoreModules");
    return () => {
      if (!cancelLoadRef.current) return;
      cancelLoadRef.current();
      cancelLoadRef.current = null;
    };
  }, []);
  useEffect(() => {
    onAnalyticsIdChange(analyticsId);
  }, [props.onAnalyticsIdChange, analyticsId]);
  const incrementLoadingCountHandler = useCallback(() => {
    if (loadingCountRef.current === 0) {
      console.log("[Shell] Life Cycle Locked, Waiting...");
      setLocked(true);
    }
    loadingCountRef.current++;
  }, []);
  const decrementLoadingCountHandler = useCallback(() => {
    if (loadingCountRef.current === 0) return;
    loadingCountRef.current--;
    if (loadingCountRef.current === 0) {
      console.log("[Shell] Life Cycle Unlocked, Resuming");
      setLocked(false);
    }
  }, []);
  const modules = useMemo(() => [...appModules.current || [], ...coreModules.current || []], [coreModules.current, appModules.current]);
  const rootModule = useMemo(() => modules ? modules.filter((module) => !!module.index)[0] || null : null, [modules]);
  const registerDynamicChannelSubscription = useCallback(
    (moduleId, channelId, subscription) => {
      if (moduleId === null) moduleId = "_null_";
      const storedModuleId = moduleId === null ? "_null_" : moduleId;
      const { module: info, component: Component } = subscription;
      const module = modules.find((module2) => module2.info === info);
      if (!module) {
        return () => {
        };
      }
      const containerProvider = async () => {
        var _a;
        switch (module.version) {
          case 1:
          case void 0:
            return module.container;
          case 2:
            return await ((_a = module.container) == null ? void 0 : _a.call(module));
          default:
            return void 0;
        }
      };
      const wrappedComponent = () => /* @__PURE__ */ jsx(OwnerModuleContext.Provider, { value: info, children: /* @__PURE__ */ jsx(AsyncWrapper, { resolver: containerProvider, children: /* @__PURE__ */ jsx(Component, {}) }) });
      const finalSubscription = {
        ...subscription,
        component: wrappedComponent
      };
      setDynamicSubscriptions((prev) => {
        var _a;
        return {
          ...prev,
          [storedModuleId]: {
            ...prev == null ? void 0 : prev[storedModuleId],
            [channelId]: [...((_a = prev == null ? void 0 : prev[storedModuleId]) == null ? void 0 : _a[channelId]) || [], finalSubscription]
          }
        };
      });
      return () => {
        setDynamicSubscriptions((prev) => {
          var _a, _b;
          return {
            ...prev,
            [storedModuleId]: {
              ...prev == null ? void 0 : prev[storedModuleId],
              [channelId]: (_b = (_a = prev == null ? void 0 : prev[storedModuleId]) == null ? void 0 : _a[channelId]) == null ? void 0 : _b.filter((x) => x !== finalSubscription)
            }
          };
        });
      };
    },
    [modules]
  );
  const getDynamicChannelSubscriptions = useCallback(
    (moduleId, channelId) => {
      if (moduleId === null) moduleId = "_null_";
      const channels = dynamicSubscriptions[moduleId];
      if (!channels) return EmptyArray;
      return channels[channelId] || EmptyArray;
    },
    [dynamicSubscriptions]
  );
  const authContextValue = useMemo(() => {
    return {
      authToken,
      authRefreshToken,
      setAuthToken,
      setAuthRefreshToken,
      permissions,
      setPermissions
    };
  }, [authToken, authRefreshToken, permissions]);
  const notifyLogoutStartedHandler = useCallback(() => {
    setIsLoggingOut(true);
    if (cancelLoadRef.current) {
      cancelLoadRef.current();
      cancelLoadRef.current = null;
    }
  }, []);
  const shellContextValue = useMemo(() => {
    let trimmedPath = path;
    if (path.endsWith("/")) trimmedPath = path.substr(0, path.length - 1);
    return {
      rootPath: path,
      moduleRootPath: `${trimmedPath}/module`,
      rootModule: rootModule ? rootModule.info || null : null,
      modules,
      incrementLoadingCount: incrementLoadingCountHandler,
      decrementLoadingCount: decrementLoadingCountHandler,
      getChannelSubscriptions: (moduleId, channelId) => {
        const staticSubscriptions = [
          ...appChannelSubscriptions ? appChannelSubscriptions(moduleId, channelId) : [],
          ...coreChannelSubscriptions ? coreChannelSubscriptions(moduleId, channelId) : []
        ];
        staticSubscriptions.sort((a, b) => a.priority - b.priority);
        return [...staticSubscriptions, ...getDynamicChannelSubscriptions(moduleId, channelId)];
      },
      registerDynamicChannelSubscription,
      setAnalyticsId: (analyticsId2) => {
        setAnalyticsId(analyticsId2 || null);
      },
      isLoggingOut,
      notifyLogoutStarted: notifyLogoutStartedHandler
    };
  }, [
    path,
    modules,
    rootModule,
    coreChannelSubscriptions,
    appChannelSubscriptions,
    getDynamicChannelSubscriptions,
    registerDynamicChannelSubscription,
    incrementLoadingCountHandler,
    decrementLoadingCountHandler,
    setAnalyticsId,
    isLoggingOut,
    notifyLogoutStartedHandler
  ]);
  return /* @__PURE__ */ jsx(TooltipContainer, { children: /* @__PURE__ */ jsx(OverlayContainer, { children: /* @__PURE__ */ jsx(HotkeyRegistryContext.Provider, { value: hotkeyRegistryManager, children: /* @__PURE__ */ jsx(ShellHotkeyWrapper, { children: /* @__PURE__ */ jsx(Body, { children: /* @__PURE__ */ jsx(AuthContext.Provider, { value: authContextValue, children: /* @__PURE__ */ jsx(AuthTokenProvider, { authToken, children: /* @__PURE__ */ jsxs(ShellContext.Provider, { value: shellContextValue, children: [
    !hasError && /* @__PURE__ */ jsxs(AlertProvider, { children: [
      /* @__PURE__ */ jsx("div", { className: classNames(styles.lifeCycleIndicator, "life-cycle"), children: currentLoadingStage }),
      currentLoadingStage !== "renderRoutes" ? /* @__PURE__ */ jsx(Shell.Splash, { locked, loadingState: currentLoadingStage }) : /* @__PURE__ */ jsx(Routes, { children }),
      /* @__PURE__ */ jsx(ContentChannel, { id: "global" })
    ] }),
    hasError && /* @__PURE__ */ jsx(ContentLayout, { children: /* @__PURE__ */ jsx(ErrorPage, {}) })
  ] }) }) }) }) }) }) }) });
};
Shell.Splash = (props) => {
  const { loadingState, locked } = props;
  return /* @__PURE__ */ jsx("div", { className: styles.splashRoot, children: /* @__PURE__ */ jsxs("div", { className: styles.splashContent, children: [
    /* @__PURE__ */ jsxs("div", { className: styles.splashSpinner, children: [
      /* @__PURE__ */ jsx("div", { className: styles.dot1 }),
      /* @__PURE__ */ jsx("div", { className: styles.dot2 })
    ] }),
    loadingState !== "renderRoutes" && process.env.NODE_ENV !== "production" && /* @__PURE__ */ jsx("div", { className: classNames(styles.loadingStatus, "loading-status", { [styles.locked]: locked }, styles[loadingState]) })
  ] }) });
};
